import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { StyleEnums, TimeReportingEnums } from "models/enums";
import { TimeReportingFormTypes } from "models/formTypes";
import { DateConstants, FormFieldConstants, SalaryDeviationConstants, TimeReportingConstants } from "utils/constants";
import { FormikProps } from "formik";
import { WeeklyFormContainerProps } from "models/propTypes/timeReportingPropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { WintSuperkollAPIModel, WintSuperkollAPIModelTimeReporting } from "wint-api-client/dist/types";
//#endregion
//#region components
import WeeklyForm from "./weeklyForm";
import useCreateDeviationTypeDialog from "./useCreateDeviationDialog";
import TimeReportHelperInstance from "../../../timeReportHelper";
import { useFloatingMessages } from "components/common";
import { useTimeReportingService } from "serviceHooks";
import { useSalaryPermissions } from "components/salary";
//#endregion
import * as Yup from "yup";
import { DateHelper, FormHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import { SalaryDeviationsService } from "services";
import { TranslationKeys } from "translations";

const WeeklyFormContainer: ForwardRefRenderFunction<FormHandles, WeeklyFormContainerProps> = (props, ref) => {
  const timeReportingService = useTimeReportingService();
  const { onFormValidationChange, currentMetaData } = props;
  const formRef = React.useRef<FormHandles>(null);
  const { enqueueMessage } = useFloatingMessages();
  const createCustomDeviationDialog = useCreateDeviationTypeDialog();
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const { isSalaryDeviationsAvailable } = useSalaryPermissions();

  React.useImperativeHandle(ref, () =>
    formRef.current
      ? {
          ...formRef.current,
        }
      : ({} as FormHandles)
  );

  const initialValues = React.useMemo(() => {
    return (
      props.currentWeek ?? {
        workedHours: 0,
        rows: [],
      }
    );
  }, [props.currentWeek]);

  const getWeekDaysValidations = () => {
    const rowScheme = {} as any;
    DateConstants.FullWeekStartWithMonday.forEach((day) => {
      rowScheme[`${day}`] = Yup.number()
        .min(0)
        .when(
          [
            FormFieldConstants.TimeReporting.WeeklyTimeReportRow.Type,
            FormFieldConstants.TimeReporting.WeeklyTimeReportRow.DeviationType,
          ],
          ([rowType, deviationType], numberScheme: Yup.NumberSchema) => {
            return deviationType === WintSuperkollAPIModel.SalaryDeviationType.CustomTimeDeviation
              ? numberScheme
              : rowType === TimeReportingEnums.TimeReportType.deviation
              ? numberScheme.max(8)
              : numberScheme.max(24);
          }
        );
    });
    return rowScheme;
  };

  const validationSchema = Yup.object<TimeReportingFormTypes.WeeklyTimeReport>({
    workedHours: Yup.number(),
    rows: Yup.array().of(
      Yup.object<TimeReportingFormTypes.WeeklyTimeReportRow>({
        ...getWeekDaysValidations(),
        name: Yup.string(),
        categoryName: Yup.string(),
        categoryId: Yup.number(),
        childName: Yup.string().when(
          FormFieldConstants.TimeReporting.WeeklyTimeReportRow.DeviationType,
          ([deviationType], scheme: Yup.StringSchema) =>
            deviationType === WintSuperkollAPIModel.SalaryDeviationType.ParentalLeave ||
            deviationType === WintSuperkollAPIModel.SalaryDeviationType.TenDays
              ? scheme.required()
              : scheme
        ),
      })
    ),
  });

  const [allowedDeviationTypes] = useSelector((state: AppState) => [state.auth.selectedCompany.allowedDeviationTypes]);

  const enabledLeaveTypes = SalaryDeviationConstants.LeaveTypes.filter((value) =>
    allowedDeviationTypes?.includes(value)
  );

  const availableProjects = React.useMemo(() => {
    const thisWeek = currentMetaData.weeks && currentMetaData.weeks[0];
    return thisWeek
      ? currentMetaData.projects?.filter((proj) =>
          TimeReportHelperInstance.isWeekFallInPeriod(thisWeek, proj.startDate, proj.endDate)
        )
      : [];
  }, [currentMetaData]);

  const getAvailableProjects = async (
    value: string,
    formattedValue: string,
    formikProps: FormikProps<TimeReportingFormTypes.WeeklyTimeReport>
  ) => {
    const { values } = formikProps;
    const remainingProjects: AutocompleteListItem[] | undefined = availableProjects
      ?.filter((proj) => getRemainingCategories(proj, values)?.length)
      .map((proj) => ({ id: proj.id, value: proj.name ?? "", data: proj }));
    return remainingProjects ?? [];
  };

  const getAvailableCategoriesForProject = async (
    formikProps: FormikProps<TimeReportingFormTypes.WeeklyTimeReport>,
    projectId?: string
  ) => {
    const { values } = formikProps;
    const currentProject = availableProjects?.find((proj) => proj.id === projectId);
    const remainingCategories = currentProject && getRemainingCategories(currentProject, values);
    const availableCategories: AutocompleteListItem[] | undefined = remainingCategories?.map((c) => ({
      id: c.id,
      value: c.name ?? "",
      data: c,
    }));

    return availableCategories ?? [];
  };

  const getAvailableDeviations = async (
    value: string,
    formattedValue: string,
    formikProps: FormikProps<TimeReportingFormTypes.WeeklyTimeReport>
  ) => {
    const { values } = formikProps;
    const remainingDeviations: AutocompleteListItem[] | undefined = enabledLeaveTypes
      ?.filter(
        (dev) =>
          dev === WintSuperkollAPIModel.SalaryDeviationType.ParentalLeave ||
          dev === WintSuperkollAPIModel.SalaryDeviationType.TenDays ||
          dev === WintSuperkollAPIModel.SalaryDeviationType.CustomTimeDeviation ||
          !values.rows.some((r) => r.deviationType === dev)
      )
      .map((deviation) => ({
        id: deviation,
        value: LocalizationHelperInstance.getFormattedMessage(`SalaryDeviation.Type_${deviation}`),
      }));
    return remainingDeviations ?? [];
  };

  const onProjectSelect = (
    item: AutocompleteListItem,
    name: string,
    formikProps: FormikProps<TimeReportingFormTypes.WeeklyTimeReport>
  ) => {
    const { values, setValues } = formikProps;

    const categories = getRemainingCategories(item.data as WintSuperkollAPIModelTimeReporting.AvailableProject, values);

    if (categories) {
      const defaultCategory = categories?.find((c) => c.isDefault) ?? categories[0];

      const newRow = getNewProjectCategoryRow(
        item.value,
        item.id?.toString(),
        defaultCategory?.name,
        defaultCategory.id,
        item.data.isInternal
      );
      setValues({ ...values, rows: [...values.rows, newRow], projectSuggestions: undefined });
    }
  };

  const onDeviationSelect = (
    item: AutocompleteListItem,
    name: string,
    formikProps: FormikProps<TimeReportingFormTypes.WeeklyTimeReport>
  ) => {
    // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
    if (item.id == WintSuperkollAPIModel.SalaryDeviationType.CustomTimeDeviation) {
      dispatch(displayDialog(createCustomDeviationDialog(onSubmitCustomDeviation)));
    } else {
      item.id && setTimeReportRow(item.value, +item.id);
    }
  };
  const onSubmitCustomDeviation = (values: TimeReportingFormTypes.CreateDeviationType) => {
    const type = WintSuperkollAPIModel.SalaryDeviationType.CustomTimeDeviation;
    const name = SalaryDeviationsService.getSalaryDeviationTypeText(type, values.name);
    setTimeReportRow(name, type, values.name, values.comment);
  };

  const onProjectCateogrySelected = (
    formikProps: FormikProps<TimeReportingFormTypes.WeeklyTimeReport>,
    item: AutocompleteListItem | null,
    rowIndex?: number
  ) => {
    const { setFieldValue, values } = formikProps;
    // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
    if (rowIndex != undefined) {
      const updatedRows = values.rows.map((ur, index) =>
        rowIndex === index ? { ...ur, categoryId: item?.id?.toString(), categoryName: item?.value } : ur
      );
      setFieldValue(FormFieldConstants.TimeReporting.WeeklyTimeReport.Rows, updatedRows);
    }
  };

  const isCategorySelectionDisabled = (
    formikProps: FormikProps<TimeReportingFormTypes.WeeklyTimeReport>,
    rowIndex?: number
  ) => {
    const { values } = formikProps;
    const row = values.rows.find((_, index) => index === rowIndex);
    if (row) {
      const project = availableProjects?.find((proj) => proj.id === row.projectId);
      const { values } = formikProps;
      return (
        (row.weekLocks && Object.entries(row.weekLocks).some(([day, val]) => val)) ||
        !(project && getRemainingCategories(project, values)?.length)
      );
    }

    return false;
  };

  const getRemainingCategories = (
    project: WintSuperkollAPIModelTimeReporting.AvailableProject,
    weeklyTimeReport: TimeReportingFormTypes.WeeklyTimeReport
  ) => {
    return project.categories?.filter(
      (c) =>
        isCategoryActive(c) && !weeklyTimeReport.rows.some((r) => r.projectId === project.id && r.categoryId === c.id)
    );
  };

  const isCategoryActive = (category: WintSuperkollAPIModelTimeReporting.TimeReportProjectCategory) => {
    const thisWeek = currentMetaData.weeks && currentMetaData.weeks[0];
    return thisWeek && TimeReportHelperInstance.isWeekFallInPeriod(thisWeek, category.startDate, category.endDate);
  };

  const setTimeReportRow = (name: string, type: number, customName?: string, customDescription?: string) => {
    const values = formRef.current?.getValues && formRef.current.getValues();

    const newRow = getNewDeviationRow(name, type, customName, customDescription);

    formRef.current?.setValues &&
      formRef.current.setValues({ ...values, rows: [...values.rows, newRow], deviationSuggestions: undefined });
  };

  const onCopyLastWeek = (includeHours: boolean) => {
    if (props.currentWeekDateRange?.start) {
      const lastWeekStartDate = DateHelper.addDays(props.currentWeekDateRange.start, -7);
      timeReportingService
        .filter(currentMetaData.employeeId, lastWeekStartDate, DateHelper.getLastDayOfWeek(lastWeekStartDate))
        .then((res) => {
          copyLastWeekTimeReportingData(res, includeHours);
        });
    }
  };

  const copyLastWeekTimeReportingData = (
    metaData: WintSuperkollAPIModelTimeReporting.TimeReportMetaDataWithPeriods,
    includeHours: boolean
  ) => {
    if (!metaData.weeks) {
      return;
    }

    const rows: TimeReportingFormTypes.WeeklyTimeReportRow[] = [];
    const inactiveCategories = new Set<string>();

    metaData.weeks[0].timeReports?.forEach((day) => {
      const weekDayName =
        day.weekDayName !== undefined && DateConstants.FullWeekStartWithSunday[Number(day.weekDayName)];

      if (!weekDayName) {
        return;
      }

      day.timeLogDate = DateHelper.addDays(day.timeLogDate, 7);

      day.projectTimes?.forEach((pt) => {
        pt.categoryTimes?.forEach((ct) => {
          const category = metaData.projects
            ?.find((proj) => proj.id === pt.projectId)
            ?.categories?.find((c) => c.id === ct.categoryId);

          if (
            category?.endDate &&
            !(
              DateHelper.isBefore(new Date(day.timeLogDate), new Date(category?.endDate)) ||
              DateHelper.isEqual(new Date(day.timeLogDate), new Date(category?.endDate))
            )
          ) {
            inactiveCategories.add(`${pt.projectName} ${ct.categoryName}`);
            return;
          }

          let row = rows.find((r) => r.projectId === pt.projectId && r.categoryId === ct.categoryId);

          if (row) {
            if (includeHours) {
              (row as any)[weekDayName] = ((row as any)[weekDayName] ?? 0) + ct.hours;
            }
          } else {
            row = getNewProjectCategoryRow(pt.projectName, pt.projectId, ct.categoryName, ct.categoryId, pt.isInternal);
            rows.push(includeHours ? { ...row, [weekDayName]: ct.hours } : { ...row });
          }
        });
      });

      // Deviations are inherently tied to hours, so they are only copied when hours are included.
      if (includeHours) {
        day.deviations?.forEach((deviation) => {
          let row = rows.find((r) => TimeReportHelperInstance.isExistingWeeklyDeviationRow(deviation, r)) as any;

          if (row) {
            (row as any)[weekDayName] = ((row as any)[weekDayName] ?? 0) + deviation.hours;
          } else {
            const name = SalaryDeviationsService.getSalaryDeviationTypeText(
              deviation.deviationType,
              deviation.customDeviationName
            );
            row = getNewDeviationRow(
              name,
              deviation.deviationType,
              deviation.customDeviationName,
              deviation.customDeviationComment
            );

            rows.push({ ...row, [weekDayName]: deviation.hours, childName: deviation.childName });
          }
        });
      }
    });

    if (rows.length === 0) {
      if (inactiveCategories.size === 0) {
        if (includeHours) {
          const emptyWeekMessage = LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.TimeReporting.PreviousWeekEmptyMessage
          );
          enqueueMessage(emptyWeekMessage, StyleEnums.DisplayType.Danger, true, false);
        } else {
          // Copying without hours only brings over projects; the previous week may still have had
          // deviations, so avoid the misleading "previous week was empty" message here.
          const noProjectsMessage = LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.TimeReporting.CopyPreviousWeekNoProjectsMessage
          );
          enqueueMessage(noProjectsMessage, StyleEnums.DisplayType.Info, true, false);
        }
      }
    } else {
      const values =
        formRef.current?.getValues && (formRef.current.getValues() as TimeReportingFormTypes.WeeklyTimeReport);

      let headerValues = { ...values?.headerRow } as TimeReportingFormTypes.WeeklyTimeReportRow;
      headerValues = calculateHeaderRowTotal(rows, headerValues);

      formRef.current?.setValues && formRef.current.setValues({ ...values, rows: rows, headerRow: headerValues });
    }

    if (inactiveCategories.size > 0) {
      const message = LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.TimeReporting.CopyPreviousWeekFailedMessage,
        "",
        { inactiveProjectCategories: Array.from(inactiveCategories).join("\n") }
      );
      enqueueMessage(message, StyleEnums.DisplayType.Info, false, true);
    }
  };

  const getNewDeviationRow = (
    name: string,
    type: number,
    customName?: string,
    customDescription?: string
  ): TimeReportingFormTypes.WeeklyTimeReportRow => {
    return {
      name: name,
      customName: customName ?? "",
      customDescription: customDescription ?? "",
      type: TimeReportingEnums.TimeReportType.deviation,
      deviationType: type,
      uniqueId: UtilsHelper.getUniqueId(),
    };
  };

  const getNewProjectCategoryRow = (
    name?: string,
    id?: string,
    categoryName?: string,
    categoryId?: number,
    isInternal?: boolean
  ): TimeReportingFormTypes.WeeklyTimeReportRow => {
    return {
      name: name ?? "",
      type: TimeReportingEnums.TimeReportType.project,
      projectId: id,
      categoryId: categoryId,
      categoryName: categoryName,
      uniqueId: UtilsHelper.getUniqueId(),
      isInternal: isInternal,
      comments: {},
    };
  };

  const onDayHoursChange = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<TimeReportingFormTypes.WeeklyTimeReport>,
    userInput?: boolean | undefined
  ) => {
    const { values, setFieldValue } = formikProps;

    const fieldInfo = FormHelper.getTableRowFieldInfo(name);

    if (fieldInfo?.innerFieldName && userInput) {
      const previousTotal = Number((values.headerRow as any)[fieldInfo?.innerFieldName]) ?? 0;
      const total = fieldInfo?.innerFieldName
        ? values.rows.map((r) => Number((r as any)[fieldInfo?.innerFieldName] ?? 0)).reduce((a, b) => +a + +b, 0)
        : 0;
      if (total !== previousTotal) {
        setFieldValue(FormFieldConstants.TimeReporting.WeeklyTimeReport.HeaderRow, {
          ...values.headerRow,
          [fieldInfo?.innerFieldName]: Number(total),
        });
      }
      if (!updatedValue || updatedValue <= 0) {
        let updatedRows = [...values.rows];
        updatedRows = updatedRows?.map((ur, index) => {
          if (index !== fieldInfo.rowIndex) {
            return ur;
          }
          return {
            ...ur,
            comments: {
              ...ur.comments,
              [`internal${fieldInfo?.innerFieldName}`]: "",
              [`external${fieldInfo?.innerFieldName}`]: "",
            },
          };
        });
        setFieldValue(FormFieldConstants.TimeReporting.WeeklyTimeReport.Rows, updatedRows);
      }
    }
  };

  const onRowRemove = (
    removedRow: TimeReportingFormTypes.WeeklyTimeReportRow,
    formikProps: FormikProps<TimeReportingFormTypes.WeeklyTimeReport>
  ) => {
    const { values, setFieldValue } = formikProps;
    const filterdRows = values.rows.filter((row) => row.uniqueId !== removedRow.uniqueId);
    let headerRow = { ...values.headerRow } as TimeReportingFormTypes.WeeklyTimeReportRow;
    headerRow = calculateHeaderRowTotal(filterdRows, headerRow);
    setFieldValue(FormFieldConstants.TimeReporting.WeeklyTimeReport.HeaderRow, headerRow);
  };

  const calculateHeaderRowTotal = (
    timeReportRows: TimeReportingFormTypes.WeeklyTimeReportRow[],
    headerRow: TimeReportingFormTypes.WeeklyTimeReportRow
  ) => {
    DateConstants.FullWeekStartWithMonday.forEach((day) => {
      const dayTotal = timeReportRows.map((row) => Number((row as any)[day] ?? 0)).reduce((a, b) => +a + +b, 0);
      (headerRow as any)[day] = dayTotal ?? 0;
    });

    return headerRow;
  };

  const handleSubmit = async (values: TimeReportingFormTypes.WeeklyTimeReport) => {
    if (currentMetaData?.weeks && currentMetaData.weeks.length === 1 && currentMetaData?.weeks[0].timeReports) {
      const postModelWeek = [...currentMetaData?.weeks[0].timeReports];
      postModelWeek.forEach((day, index) => {
        let totalHours = 0;
        const weekDayName = DateConstants.FullWeekStartWithSunday[day.weekDayName];

        const projectTimes: WintSuperkollAPIModelTimeReporting.ProjectTime[] = [];

        values?.rows
          .filter(
            (row) => row.type === TimeReportingEnums.TimeReportType.project && Number((row as any)[weekDayName]) > 0
          )
          .forEach((row) => {
            let projectTime = projectTimes.find((pt) => pt.projectId === row.projectId);

            if (!projectTime) {
              projectTime =
                day.projectTimes?.find((pt) => pt.projectId === row.projectId) ??
                ({ isInternal: false, isLocked: false } as WintSuperkollAPIModelTimeReporting.ProjectTime);

              projectTime.categoryTimes = [];
              projectTime.hours = 0;
              projectTime.projectId = row.projectId;
              projectTimes.push(projectTime);
            }

            if (row.categoryId) {
              const categoryHours = Number((row as any)[weekDayName]) ?? 0;
              projectTime.hours += categoryHours;
              totalHours += projectTime.hours;

              const categoryTime: WintSuperkollAPIModelTimeReporting.ProjectCategoryTime = {
                categoryId: row.categoryId,
                hours: categoryHours,
                isLocked: false,
              };

              if (row.comments) {
                const internalComment = (row.comments as any)[`internal${weekDayName}`];
                categoryTime.internalComment = internalComment;

                const externalComment = (row.comments as any)[`external${weekDayName}`];
                categoryTime.externalComment = externalComment;
              }

              projectTime.categoryTimes?.push(categoryTime);
            }
          });

        day.projectTimes = projectTimes;

        const deviations = values?.rows
          .filter(
            (row) => row.type === TimeReportingEnums.TimeReportType.deviation && Number((row as any)[weekDayName]) > 0
          )
          .map((row) => {
            const devHours = Number((row as any)[weekDayName]) ?? 0;
            totalHours += devHours;
            const deviation = day.deviations?.find(
              (d) => d.deviationType === row.deviationType && d.deviationId === row.deviationId
            ) ?? {
              isLocked: false,
            };
            return {
              ...deviation,
              hours: devHours,
              deviationType: row.deviationType,
              customDeviationName: row.customName,
              customDeviationComment: row.customDescription,
              childName: row.childName,
            } as WintSuperkollAPIModelTimeReporting.TimeReportDeviation;
          });
        day.deviations = deviations;
        day.totalHours = totalHours;
      });

      const updatedPeriod = { ...currentMetaData.weeks[0] };
      await timeReportingService
        .mergeWeeklyTimeReport({
          ...currentMetaData,
          weeks: [{ ...updatedPeriod, timeReports: postModelWeek }],
        })
        .catch((error) => {
          const message =
            // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
            error?.status == 403
              ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.NotEnoughPermissions)
              : error?.message && error.message.length > 0
              ? LocalizationHelperInstance.getFormattedMessage(error.message)
              : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.SomethingWentWrong);

          if (
            error?.codes &&
            error.codes.length === 1 &&
            error.codes[0] === TimeReportingConstants.AdminWarningForAlteringDeviationsWhenMonthSubmitted
          ) {
            enqueueMessage(message, StyleEnums.DisplayType.Warning);
          } else {
            enqueueMessage(message, StyleEnums.DisplayType.Danger);
            return Promise.reject(message);
          }
        });
    }
  };

  const getRowDisabled = (row: TimeReportingFormTypes.WeeklyTimeReportRow) => {
    if (row.weekLocks) {
      const totalEntries = Object.entries(row.weekLocks);
      return (
        totalEntries.length === DateConstants.FullWeekStartWithMonday.length && totalEntries.every(([day, val]) => val)
      );
    }
    return false;
  };

  const getRowHideActions = (row: TimeReportingFormTypes.WeeklyTimeReportRow) => {
    return !!(
      (row.weekLocks && Object.entries(row.weekLocks).some(([day, val]) => val)) ||
      (props.weekLockEntries &&
        Object.entries(props.weekLockEntries).some(([day, val]) => val) &&
        (row.deviationId || row.id))
    );
  };

  return (
    <WeeklyForm
      ref={formRef}
      validationSchema={validationSchema}
      defaultValues={initialValues}
      handleSubmit={handleSubmit}
      tableHeaderValues={initialValues.tableHeader}
      headerRowValues={initialValues.headerRow}
      availableProjects={getAvailableProjects}
      availableDeviations={getAvailableDeviations}
      availableCategories={getAvailableCategoriesForProject}
      onProjectSelect={onProjectSelect}
      onDeviationSelect={onDeviationSelect}
      onDayHoursChange={onDayHoursChange}
      onFormValidationChange={onFormValidationChange}
      weekLockEntries={props.weekLockEntries}
      onFormUpdate={props.onFormUpdate}
      onRowRemove={onRowRemove}
      salaryDeviationActive={isSalaryDeviationsAvailable}
      holidays={initialValues.holidays}
      isCategorySelectionDisabled={isCategorySelectionDisabled}
      onProjectCategorySelected={onProjectCateogrySelected}
      checkRowDisabled={getRowDisabled}
      checkRowHideActions={getRowHideActions}
      currentWeekDateRange={props.currentWeekDateRange}
      isCurrentWeekEmpty={props.isCurrentWeekEmpty}
      onCopyLastWeek={onCopyLastWeek}
    />
  );
};

export default forwardRef(WeeklyFormContainer);
